class MapRenderer {
  /**
   * @typedef {Object} MapRendererOptions
   * @property {HTMLElement} container
   * @property {Object[]} geojson
   * @property {string} style
   * @property {import("maplibre-gl").LngLatLike} center
   * @property {number} zoom
   * @property {number} maxZoom
   * @property {number} padding
   * @property {number} duration
   * @property {number} curve
   * @property {string} mapId
   * @property {CampFilters} filters
   * @property {Object} features
   * @property {Boolean} features.popups
   */

  /**
   * @type {import("maplibre-gl").Map}
   */
  map = null;

  /**
   * @type {Object[]}
   * Holds the full list of camps, as loaded from the GeoJSON source.
   */
  campsData = [];

  /**
   * @type {Object[]}
   * Holds a list of camps that match the current filters.
   */
  filteredCamps = [];

  /**
   * @typedef {Object} CampFilters
   * @property {number | string} week
   * @property {string} sport
   * @property {string} city
   */

  /**
   * @type {string}
   */
  style = '';

  /**
   * @type {import("maplibre-gl").Popup[]}
   */
  popups = [];

  /**
   * @type {MapRendererOptions}
   */
  __defaults = {
    center: {
      lat: -25.140133192316647,
      lng: 133.39331267545955,
    },
    style: 'https://tiles.openfreemap.org/styles/bright',
    zoom: 11,
    maxZoom: 16,
    padding: 100,
    curve: 1,
    duration: 1750,
    filters: {
      week: 'all',
      sport: window?.theme?.campFinderWidget?.pageSportName ?? 'all',
      city: window?.theme.campFinderWidget?.firstCity ?? 'all',
    },
    features: {
      popups: true,
    },
  };

  /**
   * @type {MapRendererOptions}
   */
  config = {};

  /**
   * @param {MapRendererOptions} opts
   */
  constructor(opts) {
    opts = { ...this.__defaults, ...opts };

    opts.filters = { ...this.__defaults.filters, ...opts.filters };

    this.config = opts;

    if (!this.config.container) {
      throw new ReferenceError('No container found');
    }

    this.campsData = opts.geojson;

    const { Map } = maplibregl;

    this.map = new Map({
      container: this.config.container,
      style: this.config.style,
      attributionControl: false,
      center: {
        lat: this.config.center.lat,
        lng: this.config.center.lng,
      },
      zoom: this.config.zoom,
      mapId: 'e0d4f7b4b7d1f1a4',
      cooperativeGestures: true,
    });

    this.postInit();
  }

  /**
   *
   * @param {Object} opts
   *
   * @param {Boolean} [opts.autocenter] Whether to automatically fly over to the filtered camps
   *
   * @param {import("maplibre-gl").LngLat} [opts.fallback] A fallback center if the filtered camps are empty.
   */
  filterCamps(opts) {
    let __defaults = {
      autocenter: true,
      fallback: null,
    };

    opts = { ...__defaults, ...opts };

    console.debug('filterCamps', this.config.filters, this.campsData);
    this.filteredCamps = { ...this.campsData };

    this.filteredCamps.features = this.filteredCamps.features.filter((camp) => {
      return (
        (this.config.filters.week === 'all' ||
          camp.properties.week.toLowerCase() ===
            this.config.filters.week.toLowerCase()) &&
        (this.config.filters.sport === 'all' ||
          camp.properties.sport.toLowerCase() ===
            this.config.filters.sport.toLowerCase()) &&
        (this.config.filters.city === 'all' ||
          camp.properties.city.toLowerCase() ===
            this.config.filters.city.toLowerCase())
      );
    });

    console.debug('filteredCamps', this.filteredCamps);

    this.map.getSource('camps').setData(this.filteredCamps);

    this.popups.forEach((popup) => popup.remove());
    this.popups = [];

    if (opts.autocenter) this.centerOnMarkers({ fallback: opts.fallback });

    this.getMap()
      .getContainer()
      .dispatchEvent(
        new CustomEvent('map:filtered-camps-updated', {
          detail: this.filteredCamps,
        }),
      );
  }

  /**
   *
   * @param {Object} prices
   *
   * @param {String} prices.standard
   * @param {String} [prices.compare_at_price]
   */
  generatePricesHtml(prices) {
    const formatter = new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    let retVal = `<span class="current-price">${formatter.format(prices.standard)}</span>`;

    if (prices.compare_at_price) {
      retVal += `<span class="compare-at-price">${formatter.format(prices.compare_at_price)}</span>`;
    }

    return retVal;
  }

  generatePopupContent(e) {
    const { camp_type, id, location_name, sport, suburb, camp_dates, url } =
      e.features[0].properties;

    let popupTitle =
      camp_type === 'asc'
        ? window.theme.strings.asc_title_template
        : camp_type === 'partner'
          ? window.theme.strings.partner_title_template
          : window.theme.strings.community_title_template;

    popupTitle = popupTitle.replace('{{ sport_name }}', sport);

    const prices = this.generatePricesHtml({
      standard: e.features[0].properties.price,
      compare_at_price: e.features[0].properties.compare_at_price,
    });

    let compiled = `<strong class="popup-title">${popupTitle}</strong>
    <p class="popup-location">${location_name}, ${suburb}</p>
    <p class="popup-camp-dates">${camp_dates}</p>
    
    <div class="popup-actions">
    <div class="popup-prices">
      ${prices}
      </div>
      <a href="${url}"><svg
  xmlns='http://www.w3.org/2000/svg'
  width='32'
  height='32'
  viewBox='0 0 32 32'
  fill='none'
  class="icon"
>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M1.99821 16.9987C1.99821 16.7335 2.10357 16.4792 2.29111 16.2916C2.47864 16.1041 2.733 15.9987 2.99821 15.9987L26.5842 15.9987L20.2902 9.70674C20.1024 9.51896 19.9969 9.26429 19.9969 8.99874C19.9969 8.73319 20.1024 8.47851 20.2902 8.29074C20.478 8.10296 20.7327 7.99747 20.9982 7.99747C21.2638 7.99747 21.5184 8.10296 21.7062 8.29074L29.7062 16.2907C29.7993 16.3836 29.8732 16.494 29.9236 16.6155C29.9741 16.737 30 16.8672 30 16.9987C30 17.1303 29.9741 17.2605 29.9236 17.382C29.8732 17.5035 29.7993 17.6138 29.7062 17.7067L21.7062 25.7067C21.6132 25.7997 21.5029 25.8735 21.3814 25.9238C21.2599 25.9741 21.1297 26 20.9982 26C20.7327 26 20.478 25.8945 20.2902 25.7067C20.1024 25.519 19.9969 25.2643 19.9969 24.9987C19.9969 24.7332 20.1024 24.4785 20.2902 24.2907L26.5842 17.9987L2.99821 17.9987C2.733 17.9987 2.47864 17.8934 2.29111 17.7058C2.10357 17.5183 1.99821 17.264 1.99821 16.9987Z" fill="#FF6600"/>
</svg>
</a>
    </div>`;

    return {
      title: popupTitle,
      compiled,
    };
  }

  onPinClick(e) {
    const coordinates = e.features[0].geometry.coordinates.slice();

    this.map.flyTo({
      center: coordinates,
      zoom: this.config.maxZoom,
      duration: this.config.duration,
      curve: this.config.curve,
      padding: this.config.padding,
    });

    this.getMap()
      .getContainer()
      .dispatchEvent(
        new CustomEvent('map:pin-click', {
          detail: e.features[0],
        }),
      );

    if (this.config.features.popups === true) {
      const { compiled } = this.generatePopupContent(e);

      let popup = new maplibregl.Popup()
        .setLngLat(coordinates)
        .setMaxWidth('unset')
        .setOffset([0, -15])
        .setHTML(compiled)
        .addTo(this.map);

      popup.on('close', (e) => {
        this.getMap()
          .getContainer()
          .dispatchEvent(new CustomEvent('map:popup-closed', { detail: e }));
      });

      popup.on('open', (e) => {
        this.getMap()
          .getContainer()
          .dispatchEvent(
            new CustomEvent('map:popup-opened', {
              detail: e,
            }),
          );
      });

      this.popups.push(popup);
    }
  }

  assignHoverCursor(layer, cursor) {
    this.map.on('mouseenter', layer, () => {
      this.map.getCanvas().style.cursor = cursor;
    });

    this.map.on('mouseleave', layer, () => {
      this.map.getCanvas().style.cursor = '';
    });
  }

  postInit() {
    this.map.on('load', async () => {
      this.svgManager = new maplibregl.SvgManager(this.map);

      if (this.campsData) {
        this.svgManager.add('asc-pin', window.theme.campFinder.ascPinUrl);
        this.svgManager.add('csc-pin', window.theme.campFinder.cscPinUrl);
        this.svgManager.add(
          'partner-pin',
          window.theme.campFinder.partnerPinUrl,
        );

        console.debug('campsData', this.campsData);

        this.map.addSource('camps', {
          type: 'geojson',
          data: this.campsData,
          cluster: true,
          clusterMaxZoom: 11,
          clusterRadius: 50,
        });

        this.map.addLayer({
          id: 'camp-clusters',
          type: 'circle',
          source: 'camps',
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': [
              'step',
              ['get', 'point_count'],
              '#e1cc8f',
              2,
              '#e1cc8f',
              3,
              '#e1cc8f',
              4,
              '#e1cc8f',
              5,
              '#e1cc8f',
            ],
            'circle-radius': 20,
          },
        });

        this.map.addLayer({
          id: 'cluster-count',
          type: 'symbol',
          source: 'camps',
          filter: ['has', 'point_count'],
          layout: {
            'text-field': '{point_count_abbreviated}',
            'text-font': ['Noto Sans Regular'],
            'text-size': 12,
          },
        });

        this.map.addLayer({
          id: 'partner-camps',
          type: 'symbol',
          source: 'camps',
          filter: [
            'all',
            ['!has', 'point_count'],
            ['==', 'camp_type', 'partner'],
          ],
          layout: {
            'icon-image': 'partner-pin',
          },
        });

        this.map.addLayer({
          id: 'csc-camps',
          type: 'symbol',
          source: 'camps',
          filter: ['all', ['!has', 'point_count'], ['==', 'camp_type', 'csc']],
          layout: {
            'icon-image': 'csc-pin',
          },
        });

        this.map.addLayer({
          id: 'asc-camps',
          type: 'symbol',
          source: 'camps',
          filter: ['all', ['!has', 'point_count'], ['==', 'camp_type', 'asc']],
          layout: {
            'icon-image': 'asc-pin',
          },
        });

        this.map.on('click', 'camp-clusters', async (e) => {
          const features = this.map.queryRenderedFeatures(e.point, {
            layers: ['camp-clusters'],
          });

          const clusterId = features[0].properties.cluster_id;
          let zoom = await this.map
            .getSource('camps')
            .getClusterExpansionZoom(clusterId);

          if (zoom === this.map.getZoom()) {
            console.debug('already at zoom', zoom);

            // TODO: Open popups
          }
          this.map.easeTo({
            center: features[0].geometry.coordinates,
            zoom,
          });
        });

        this.assignHoverCursor('camp-clusters', 'zoom-in');
        this.assignHoverCursor('asc-camps', 'pointer');
        this.assignHoverCursor('csc-camps', 'pointer');
        this.assignHoverCursor('partner-camps', 'pointer');

        this.map.on('click', 'asc-camps', (e) => this.onPinClick(e));
        this.map.on('click', 'csc-camps', (e) => this.onPinClick(e));
        this.map.on('click', 'partner-camps', (e) => this.onPinClick(e));

        this.filterCamps();
      }
    });
  }

  getMap() {
    return this.map;
  }

  getFilters() {
    return { ...this.config.filters };
  }

  /**
   * Sets the current filters and re-renders the map.
   * @param {CampFilters} filters
   *
   * @param {Object} opts
   *
   * @param {Boolean} [opts.autocenter] Whether to automatically fly over to the filtered camps
   * @param {import("maplibre-gl").LngLat} [opts.fallback] A fallback center if the filtered camps are empty.
   */
  setFilters(filters, opts) {
    this.config.filters = { ...this.__defaults.filters, ...filters };

    this.filterCamps(opts);
  }

  getFilteredCamps() {
    return { ...this.filteredCamps };
  }

  getAllCamps() {
    return { ...this.campsData };
  }

  /**
   *
   * @param {Object} opts
   * @param {import("maplibre-gl").LngLat} [opts.fallback] A fallback center if the filtered camps are empty.
   */
  centerOnMarkers(opts) {
    if (this.filteredCamps.features.length === 0) {
      if (!opts.fallback) {
        this.getMap()
          .getContainer()
          .dispatchEvent(
            new CustomEvent('map:markers-empty', {
              detail: this.getFilters(),
            }),
          );
        return;
      }

      this.map.easeTo({
        center: opts.fallback,
      });

      return;
    }
    const bounds = new maplibregl.LngLatBounds();

    this.filteredCamps.features.forEach((camp) => {
      bounds.extend(camp.geometry.coordinates);
    });

    this.map.fitBounds(bounds, {
      duration: this.config.duration,
      curve: this.config.curve,
      padding: this.config.padding,
      maxZoom: this.config.maxZoom,
    });
  }
}
